import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  AdminEntitySlug,
  ImportDiffResponse,
  exportAdminZip,
  importAdminZip,
} from '../../../api/adminImportExportApi';
import { colors, typo } from '../../../theme/tokens';
import { WriteButton } from '../../../components/ui';

/**
 * /admin/import-export — unified ZIP envelope for globals + catalogs.
 *
 * Export side: checkbox list of every admin entity. Download ZIP triggers
 * a blob-based download (so axios still attaches the bearer token).
 *
 * Import side: file picker + two buttons (Dry run, Apply). Apply is
 * disabled until a dry run has succeeded — mirrors the rest of the
 * admin-view UX (no action that writes without an explicit preview).
 *
 * The diff panel rolls a per-entity `to_insert / to_update / errors`
 * summary into a table. No per-row diff view in R7 — that would need a
 * richer server response and isn't on the critical path for parity with
 * the existing per-entity publish diff modal.
 */

interface EntityGroup {
  label: string;
  entities: { slug: AdminEntitySlug; label: string }[];
}

const GROUPS: EntityGroup[] = [
  {
    label: 'Globals',
    entities: [
      { slug: 'task', label: 'Tasks' },
      { slug: 'adventure', label: 'Adventures' },
      { slug: 'cohort', label: 'Cohorts' },
      { slug: 'sweepstakes', label: 'Sweepstakes' },
      { slug: 'terms_of_service', label: 'Terms of Service' },
      { slug: 'task_reward_collection', label: 'Reward Collections' },
      { slug: 'plan_cohort_purse_mapping', label: 'Plan-Cohort-Purse Mappings' },
      { slug: 'questionnaire', label: 'Questionnaires' },
    ],
  },
  {
    label: 'Reference Catalogs',
    entities: [
      { slug: 'task_type', label: 'Task Types' },
      { slug: 'task_category', label: 'Task Categories' },
      { slug: 'task_reward_type', label: 'Task Reward Types' },
      { slug: 'wallet_type', label: 'Wallet Types' },
      { slug: 'notification_channel', label: 'Notification Channels' },
      { slug: 'notification_category', label: 'Notification Categories' },
      { slug: 'component_type', label: 'Component Types' },
      { slug: 'onboarding_component', label: 'Onboarding Components' },
      { slug: 'canonical_field', label: 'Canonical Fields' },
      { slug: 'cohort_attribute_def', label: 'Cohort Attribute Defs' },
      { slug: 'tenant_attr_definition', label: 'Tenant Attribute Defs' },
    ],
  },
];

const ALL_SLUGS: AdminEntitySlug[] = GROUPS.flatMap((g) => g.entities.map((e) => e.slug));

const ImportExportPage: React.FC = () => {
  const [selected, setSelected] = useState<Set<AdminEntitySlug>>(new Set(ALL_SLUGS));
  const [exportBusy, setExportBusy] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [dryRunResult, setDryRunResult] = useState<ImportDiffResponse | null>(null);
  const [applyResult, setApplyResult] = useState<ImportDiffResponse | null>(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const toggleEntity = (slug: AdminEntitySlug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleGroup = (group: EntityGroup, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      group.entities.forEach((e) => {
        if (checked) next.add(e.slug);
        else next.delete(e.slug);
      });
      return next;
    });
  };

  const handleExport = async () => {
    if (selected.size === 0) {
      setSnackbar({ open: true, msg: 'Select at least one entity to export', sev: 'info' });
      return;
    }
    setExportBusy(true);
    try {
      await exportAdminZip({
        entities: selected.size === ALL_SLUGS.length ? 'all' : Array.from(selected),
      });
      setSnackbar({ open: true, msg: 'Export downloaded', sev: 'success' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSnackbar({ open: true, msg: `Export failed: ${msg}`, sev: 'error' });
    } finally {
      setExportBusy(false);
    }
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    // Reset any prior dry-run / apply state whenever a new file lands so
    // the Apply button can't accidentally operate on the old diff.
    setDryRunResult(null);
    setApplyResult(null);
  };

  const handleDryRun = async () => {
    if (!file) return;
    setDryRunBusy(true);
    setApplyResult(null);
    try {
      const res = await importAdminZip(file, 'dry-run');
      setDryRunResult(res);
      setSnackbar({ open: true, msg: 'Dry run completed', sev: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? (err instanceof Error ? err.message : String(err));
      setSnackbar({ open: true, msg: `Dry run failed: ${msg}`, sev: 'error' });
      setDryRunResult(null);
    } finally {
      setDryRunBusy(false);
    }
  };

  const handleApply = async () => {
    if (!file || !dryRunResult) return;
    setApplyBusy(true);
    try {
      const res = await importAdminZip(file, 'apply');
      setApplyResult(res);
      const hasErrors = Object.values(res.summary).some((s) => s.errors.length > 0);
      setSnackbar({
        open: true,
        msg: hasErrors ? 'Applied with per-entity errors (see diff)' : 'Applied successfully',
        sev: hasErrors ? 'info' : 'success',
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? (err instanceof Error ? err.message : String(err));
      setSnackbar({ open: true, msg: `Apply failed: ${msg}`, sev: 'error' });
    } finally {
      setApplyBusy(false);
    }
  };

  const applyEnabled = !!dryRunResult && !dryRunBusy && !applyBusy;
  const diffToShow = applyResult ?? dryRunResult;

  const totals = useMemo(() => {
    if (!diffToShow) return null;
    let insert = 0;
    let update = 0;
    let errors = 0;
    for (const slot of Object.values(diffToShow.summary)) {
      insert += slot.to_insert;
      update += slot.to_update;
      errors += slot.errors.length;
    }
    return { insert, update, errors };
  }, [diffToShow]);

  return (
    <Box sx={{ maxWidth: 1120 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Import / Export</Typography>
        <Typography sx={{ ...typo.pageSubtitle }}>
          Move globals and reference catalogs between environments. Output is a single ZIP
          envelope (scope = admin); publish still runs per entity.
        </Typography>
      </Box>

      {/* ── Export ── */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <CloudDownloadIcon sx={{ color: colors.adminAccent }} />
          <Typography sx={{ ...typo.sectionTitle, fontSize: 16 }}>Export</Typography>
        </Stack>

        <Typography sx={{ ...typo.secondaryText, mb: 2 }}>
          Select which entities to include, then download the unified ZIP.
        </Typography>

        {GROUPS.map((group) => {
          const allSelected = group.entities.every((e) => selected.has(e.slug));
          const someSelected = group.entities.some((e) => selected.has(e.slug));
          return (
            <Box key={group.label} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={(e) => toggleGroup(group, e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                    {group.label}
                  </Typography>
                }
              />
              <Box sx={{ pl: 4, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {group.entities.map((ent) => (
                  <FormControlLabel
                    key={ent.slug}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.has(ent.slug)}
                        onChange={() => toggleEntity(ent.slug)}
                      />
                    }
                    label={<Typography sx={{ fontSize: 13 }}>{ent.label}</Typography>}
                  />
                ))}
              </Box>
            </Box>
          );
        })}

        <Button
          variant="contained"
          startIcon={
            exportBusy ? (
              <CircularProgress size={16} sx={{ color: colors.bgWhite }} />
            ) : (
              <CloudDownloadIcon />
            )
          }
          disabled={exportBusy || selected.size === 0}
          onClick={handleExport}
          sx={{ mt: 1 }}
        >
          Download ZIP
        </Button>
      </Paper>

      {/* ── Import ── */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <UploadFileIcon sx={{ color: colors.adminAccent }} />
          <Typography sx={{ ...typo.sectionTitle, fontSize: 16 }}>Import</Typography>
        </Stack>

        <Typography sx={{ ...typo.secondaryText, mb: 2 }}>
          Upload a ZIP produced by Export (or another environment&apos;s Export). Dry run
          validates shape and computes the diff; Apply performs the inserts/updates. Per
          entity a failure rolls back just that entity.
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose ZIP
          </Button>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            {file ? file.name : 'No file selected'}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            data-testid="import-file-input"
          />
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={
              dryRunBusy ? (
                <CircularProgress size={16} sx={{ color: colors.bgWhite }} />
              ) : (
                <PlayArrowIcon />
              )
            }
            disabled={!file || dryRunBusy || applyBusy}
            onClick={handleDryRun}
          >
            Dry run
          </Button>
          <WriteButton
            variant="outlined"
            color="warning"
            startIcon={
              applyBusy ? (
                <CircularProgress size={16} />
              ) : (
                <CheckCircleIcon />
              )
            }
            disabled={!applyEnabled}
            onClick={handleApply}
          >
            Apply
          </WriteButton>
        </Stack>

        {diffToShow && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              <Chip
                label={`Mode: ${diffToShow.mode}`}
                size="small"
                color={diffToShow.mode === 'apply' ? 'warning' : 'default'}
              />
              {totals && (
                <>
                  <Chip label={`Insert: ${totals.insert}`} size="small" color="success" />
                  <Chip label={`Update: ${totals.update}`} size="small" color="info" />
                  <Chip
                    label={`Errors: ${totals.errors}`}
                    size="small"
                    color={totals.errors > 0 ? 'error' : 'default'}
                  />
                </>
              )}
            </Stack>

            <Box
              sx={{
                border: `1px solid ${colors.borderLight}`,
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 3fr',
                  px: 2,
                  py: 1,
                  backgroundColor: colors.bgMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                <Box>Entity</Box>
                <Box>Insert</Box>
                <Box>Update</Box>
                <Box>Skip</Box>
                <Box>Errors</Box>
              </Box>
              {Object.entries(diffToShow.summary).map(([slug, slot], idx, arr) => (
                <Box
                  key={slug}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 3fr',
                    px: 2,
                    py: 1.25,
                    fontSize: 13,
                    borderBottom:
                      idx === arr.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                  }}
                >
                  <Box sx={{ fontFamily: 'monospace' }}>{slug}</Box>
                  <Box>{slot.to_insert}</Box>
                  <Box>{slot.to_update}</Box>
                  <Box>{slot.to_skip}</Box>
                  <Box>
                    {slot.errors.length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
                        none
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {slot.errors.slice(0, 5).map((e, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.5,
                              fontSize: 12,
                              color: colors.errorText,
                            }}
                          >
                            <WarningAmberIcon sx={{ fontSize: 14, mt: 0.25 }} />
                            <code style={{ wordBreak: 'break-all' }}>{e}</code>
                          </Box>
                        ))}
                        {slot.errors.length > 5 && (
                          <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>
                            … {slot.errors.length - 5} more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImportExportPage;
